import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchTenantsWithCounts,
  mergeSelectedTenants,
  toggleTenantSelection,
  clearTenantSelections,
} from '../store/slices/adminSlice';

// Data source type icons
function DataSourceTypeIcon({ sourceType }) {
  if (sourceType === 'salesforce') {
    return (
      <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12.87 3.97c1.21-.55 2.56-.84 3.93-.84 2.24 0 4.31.78 5.92 2.09-.63.62-1.17 1.32-1.61 2.11-1.24-1.07-2.75-1.72-4.47-1.72-1.98 0-3.77.82-5.04 2.15-1.27-1.33-3.06-2.15-5.04-2.15-1.87 0-3.54.76-4.77 1.99-.15-.84-.22-1.67-.22-2.51 0-1.97.41-3.84 1.15-5.53C4.33 1.14 6.44.44 8.69.44c2.3 0 4.36.9 5.91 2.35.28-.09.55-.18.84-.25-.54-.28-1.11-.52-1.7-.71-.62.36-1.26.69-1.87 1.14zm8.04 10.05c0 3.82-3.1 6.92-6.92 6.92-3.82 0-6.92-3.1-6.92-6.92s3.1-6.92 6.92-6.92 6.92 3.1 6.92 6.92z" />
      </svg>
    );
  } else if (sourceType === 'hubspot') {
    return (
      <svg className="w-5 h-5 text-orange-600" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.072A2.198 2.198 0 0017.233.836h-.071a2.198 2.198 0 00-2.199 2.198v.071a2.199 2.199 0 001.267 1.979v2.846a4.242 4.242 0 00-2.344.774l-6.164-3.287a2.477 2.477 0 10-.913 1.062l6.164 3.287a4.315 4.315 0 00-.549 2.093 4.346 4.346 0 004.345 4.345 4.346 4.346 0 004.345-4.345A4.346 4.346 0 0018.164 7.93z"/>
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8 4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
    </svg>
  );
}

// Merge modal component
function MergeModal({ isOpen, onClose, tenants, selectedTenantIds, onMerge, isMerging }) {
  const [targetTenantId, setTargetTenantId] = useState('');

  useEffect(() => {
    if (selectedTenantIds.length > 0 && !targetTenantId) {
      setTargetTenantId(selectedTenantIds[0]);
    }
  }, [selectedTenantIds, targetTenantId]);

  if (!isOpen) return null;

  const selectedTenants = tenants.filter(t => selectedTenantIds.includes(t.tenant_id));
  const targetTenant = selectedTenants.find(t => t.tenant_id === targetTenantId);
  const sourceTenants = selectedTenants.filter(t => t.tenant_id !== targetTenantId);

  const totalUsers = selectedTenants.reduce((sum, t) => sum + t.user_count, 0);
  const totalDataSources = selectedTenants.reduce((sum, t) => sum + t.data_source_count, 0);

  const handleMerge = () => {
    if (!targetTenantId || sourceTenants.length === 0) return;
    onMerge(sourceTenants.map(t => t.tenant_id), targetTenantId);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Merge Tenants</h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <svg className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-800">This action cannot be undone</p>
                <p className="text-sm text-yellow-700 mt-1">
                  All data sources, users, and tokens will be transferred to the target tenant. Source tenants will be deleted.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Select target tenant (which one survives)</h3>
            <div className="space-y-2">
              {selectedTenants.map(tenant => (
                <label key={tenant.tenant_id} className="flex items-start p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                  <input
                    type="radio"
                    name="target-tenant"
                    value={tenant.tenant_id}
                    checked={targetTenantId === tenant.tenant_id}
                    onChange={(e) => setTargetTenantId(e.target.value)}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{tenant.name}</div>
                    <div className="text-sm text-gray-500">{tenant.tenant_id}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {tenant.user_count} users • {tenant.data_source_count} data sources
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {targetTenant && sourceTenants.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Merge Summary</h4>
              <div className="text-sm text-blue-800">
                <p>
                  <strong>{sourceTenants.length} tenant{sourceTenants.length > 1 ? 's' : ''}</strong> will be merged into <strong>{targetTenant.name}</strong>
                </p>
                <ul className="mt-2 space-y-1 ml-4 list-disc">
                  <li>{totalUsers} users will be in the merged tenant</li>
                  <li>{totalDataSources} data sources will be transferred</li>
                  <li>Duplicate users will keep highest role (admin &gt; user &gt; viewer)</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            disabled={isMerging}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleMerge}
            disabled={isMerging || !targetTenantId || sourceTenants.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMerging ? 'Merging...' : 'Merge Tenants'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main TenantsTab component
export default function TenantsTab() {
  const dispatch = useDispatch();
  const { tenantsWithCounts, selectedTenantIds, isMerging, isLoading, error } = useSelector(state => state.admin);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchTenantsWithCounts());
  }, [dispatch]);

  const handleToggleSelection = (tenantId) => {
    dispatch(toggleTenantSelection(tenantId));
  };

  const handleClearSelections = () => {
    dispatch(clearTenantSelections());
  };

  const handleMerge = async (sourceTenantIds, targetTenantId) => {
    try {
      await dispatch(mergeSelectedTenants({ sourceTenantIds, targetTenantId })).unwrap();
      setIsMergeModalOpen(false);
      // Refresh tenant list
      dispatch(fetchTenantsWithCounts());
    } catch (err) {
      // Error is already in state
      console.error('Merge failed:', err);
    }
  };

  if (isLoading && tenantsWithCounts.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tenant Management</h2>
          <p className="text-sm text-gray-600 mt-1">View and manage all tenants in the system</p>
        </div>
        {selectedTenantIds.length > 0 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">{selectedTenantIds.length} selected</span>
            <button
              onClick={handleClearSelections}
              className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={() => setIsMergeModalOpen(true)}
              disabled={selectedTenantIds.length < 2}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Merge Selected
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tenantsWithCounts.map(tenant => {
          const isSelected = selectedTenantIds.includes(tenant.tenant_id);

          return (
            <div
              key={tenant.tenant_id}
              className={`border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer ${
                isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
              }`}
              onClick={() => handleToggleSelection(tenant.tenant_id)}
            >
              <div className="flex items-start">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggleSelection(tenant.tenant_id)}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1 mr-3"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{tenant.name}</h3>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{tenant.tenant_id}</p>

                  <div className="mt-3 space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <span>{tenant.user_count} user{tenant.user_count !== 1 ? 's' : ''}</span>
                    </div>

                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                      </svg>
                      <span>{tenant.data_source_count} data source{tenant.data_source_count !== 1 ? 's' : ''}</span>
                    </div>

                    {tenant.data_sources && tenant.data_sources.length > 0 && (
                      <div className="flex items-center space-x-1 pt-2">
                        {Array.from(new Set(tenant.data_sources.map(ds => ds.source_type))).map(sourceType => (
                          <div key={sourceType} className="flex items-center">
                            <DataSourceTypeIcon sourceType={sourceType} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {tenantsWithCounts.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No tenants found</h3>
          <p className="mt-1 text-sm text-gray-500">No tenants are currently registered in the system.</p>
        </div>
      )}

      <MergeModal
        isOpen={isMergeModalOpen}
        onClose={() => setIsMergeModalOpen(false)}
        tenants={tenantsWithCounts}
        selectedTenantIds={selectedTenantIds}
        onMerge={handleMerge}
        isMerging={isMerging}
      />
    </div>
  );
}
